import {
  compact,
  EventBanner,
  type EventBannerProps,
  isDateAfter,
} from '@leland-dev/leland-ui-library';
import {
  PortableText,
  type PortableTextReactComponents,
} from '@portabletext/react';
import groq from 'groq';
import Script from 'next/script';
import { type FC, useEffect, useMemo, useState } from 'react';

import type { GoalName } from '../../../__generated-gql-types__/globalTypes';
import type {
  AssetDownload as AssetDownloadType,
  BodyText as BodyTextType,
  CallToAction as CallToActionType,
  Category,
  FloatedImage as FloatedImageType,
  ImageRow as ImageRowType,
  LelandPlusBanner as LelandPlusBannerType,
  LelandTable as LelandTableType,
  Note as NoteType,
  QuoteCallout as QuoteCalloutType,
  SanityKeyed,
  TextBreak as TextBreakType,
  TextCallout as TextCalloutType,
  TopCoaches as TopCoachesType,
  Youtube as YoutubeType,
} from '../../../types/cmsTypes';
import mxpnl from '../../../utils/analytics/mixpanel';
import phog from '../../../utils/analytics/posthog/posthog';
import { categorySlugToUrn } from '../../../utils/category';
import { APPLICANT_BASE_URL } from '../../../utils/constants';
import { getCategoryForEvent, isEventShindig } from '../../../utils/events';
import { getClassUrl, getCoachProfileUrl } from '../../../utils/routing';
import { slugify } from '../../../utils/slugify';
import { addUtmParamsFromArticle } from '../../../utils/utm';
import { ArticleAuthor } from '../ArticleAuthor';
import { ArticleCTA } from '../ArticleCTA';

import AssetDownload, {
  ASSET_DOWNLOAD_MARK_DEFS_SELECTION,
} from './AssetDownload';
import BulletList, { BulletListHtml } from './BulletList';
import CMSLink, { CMS_LINK_MARK_DEFS_SELECTION, CMSLinkHtml } from './CMSLink';
import CallToAction from './CallToAction';
import CategoryLandingPageLink, {
  CATEGORY_LANDING_PAGE_LINK_MARK_DEFS_SELECTION,
  CategoryLandingPageLinkHtml,
} from './CategoryLandingPageLink';
import CoachProfileLink, {
  COACH_PROFILE_LINK_MARK_DEFS_SELECTION,
  CoachProfileLinkHtml,
} from './CoachProfileLink';
import { DisabledOutputHtml } from './DisabledOutput';
import ExternalLink, {
  EXTERNAL_LINK_MARK_DEFS_SELECTION,
  ExternalLinkHtml,
} from './ExternalLink';
import FloatedImage, {
  FLOATED_IMAGE_MARK_DEFS_SELECTION,
  FloatedImageHtml,
} from './FloatedImage';
import H2, { H2Html } from './H2';
import H3, { H3Html } from './H3';
import ImageRow, {
  IMAGE_ROW_MARK_DEFS_SELECTION,
  ImageRowHtml,
} from './ImageRow';
import { LelandPlusBanner } from './LelandPlusBanner';
import LelandTable, { LELAND_TABLE_TYPE, LelandTableHtml } from './LelandTable';
import ListItem, { ListItemHtml } from './ListItem';
import Note, { NOTE_MARK_DEFS_SELECTION, NoteHtml } from './Note';
import NumberList, { NumberListHtml } from './NumberList';
import Paragraph, { ParagraphHtml } from './Paragraph';
import QuoteCallout, { QuoteCalloutHtml } from './QuoteCallout';
import TableOfContents, { type TableOfContentsEntry } from './TableOfContents';
import TextBreak, { TextBreakHtml } from './TextBreak';
import TextCallout, { TextCalloutHtml } from './TextCallout';
import TopCoaches, { TOP_COACHES_MARK_DEFS_SELECTION } from './TopCoaches';
import YoutubeEmbed, { YoutubeEmbedHtml } from './YoutubeEmbed';
import {
  type BodyContextCategoryFragment,
  type BodyContextClassFragment,
  type BodyContextCoachBatchResponseFragment,
  type BodyContextCoachFragment,
  type BodyContextShindigFragment,
} from './__generated-gql-types__/index.generated';
export const BODY_CONTENT_CONTENT_SELECTION = groq`
  ...select(
    _type == "bodyText" => {
      ...,
      markDefs[] {
        _type,
        _key,
        ${EXTERNAL_LINK_MARK_DEFS_SELECTION},
        ${CATEGORY_LANDING_PAGE_LINK_MARK_DEFS_SELECTION},
        ${CMS_LINK_MARK_DEFS_SELECTION},
        ${COACH_PROFILE_LINK_MARK_DEFS_SELECTION},
      }
    },
    ${TOP_COACHES_MARK_DEFS_SELECTION},
    ${ASSET_DOWNLOAD_MARK_DEFS_SELECTION},
    ${FLOATED_IMAGE_MARK_DEFS_SELECTION},
    ${IMAGE_ROW_MARK_DEFS_SELECTION},
    ${NOTE_MARK_DEFS_SELECTION},
    {...}
  )
`;

const baseHtmlComponents: Partial<PortableTextReactComponents> = {
  types: {
    floatedImage: FloatedImageHtml,
    imageRow: ImageRowHtml,
    textBreak: TextBreakHtml,
    note: NoteHtml,
    callToAction: DisabledOutputHtml,
    quoteCallout: QuoteCalloutHtml,
    textCallout: TextCalloutHtml,
    topCoaches: DisabledOutputHtml,
    youtube: YoutubeEmbedHtml,
    assetDownload: DisabledOutputHtml,
    [LELAND_TABLE_TYPE]: LelandTableHtml,
    lelandPlusBanner: DisabledOutputHtml,
    esyoh: DisabledOutputHtml,
  },
  marks: {
    link: ExternalLinkHtml,
    categoryyLink: CategoryLandingPageLinkHtml,
    cmsLink: CMSLinkHtml,
    coachLink: CoachProfileLinkHtml,
  },
  block: {
    normal: ParagraphHtml,
    h2: H2Html,
    h3: H3Html,
  },
  list: {
    bullet: BulletListHtml,
    number: NumberListHtml,
  },
  listItem: ListItemHtml,
};

const baseComponents: Partial<PortableTextReactComponents> = {
  types: {
    textBreak: TextBreak,
    callToAction: CallToAction,
    floatedImage: FloatedImage,
    imageRow: ImageRow,
    note: Note,
    quoteCallout: QuoteCallout,
    textCallout: TextCallout,
    topCoaches: TopCoaches,
    youtube: YoutubeEmbed,
    assetDownload: AssetDownload,
    [LELAND_TABLE_TYPE]: LelandTable,
  },
  marks: {
    link: ExternalLink,
    categoryyLink: CategoryLandingPageLink,
    cmsLink: CMSLink,
    coachLink: CoachProfileLink,
  },
  block: {
    normal: Paragraph,
    h2: H2,
    h3: H3,
  },
  list: {
    bullet: BulletList,
    number: NumberList,
  },
  listItem: ListItem,
};

interface BodyTextBlock {
  _type: 'bodyText';
  style?: string;
  children?: Array<{ text?: string }>;
}

interface EsyohType {
  _type: 'esyoh';
  _key: string;
  thisFieldIsNotUsed?: string;
}

// TODO fix this when we figure out why Sanity codegen isn't properly typing BodyTextBlock
type ContentBlock =
  | SanityKeyed<BodyTextType>
  | SanityKeyed<TextBreakType>
  | SanityKeyed<CallToActionType>
  | SanityKeyed<FloatedImageType>
  | SanityKeyed<ImageRowType>
  | SanityKeyed<NoteType>
  | SanityKeyed<QuoteCalloutType>
  | SanityKeyed<TextCalloutType>
  | SanityKeyed<TopCoachesType>
  | SanityKeyed<YoutubeType>
  | SanityKeyed<AssetDownloadType>
  | SanityKeyed<LelandPlusBannerType>
  | SanityKeyed<LelandTableType>
  | EsyohType
  | BodyTextBlock;

interface EventBannerParams {
  title: string;
  time?: number;
  duration?: number;
  url: string;
  coaches: Possible<BodyContextCoachFragment[]>;
  categories: Possible<BodyContextCategoryFragment[]>;
}

interface Props {
  articleSlug: Possible<string>;
  content: ContentBlock[];
  goal?: Possible<GoalName>;
  categories?: Category[];
  lelandPlusContributors: Possible<BodyContextCoachBatchResponseFragment>;
  freeEvent: Possible<BodyContextShindigFragment | BodyContextClassFragment>;
  coach?: Possible<BodyContextCoachFragment>;
}

const BodyContent: FC<Props> = ({
  articleSlug,
  content: contentProp,
  categories,
  lelandPlusContributors,
  freeEvent,
  coach,
}) => {
  const [activeSectionId, setActiveSectionId] =
    useState<Possible<string>>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        });
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0.1 },
    );

    document.querySelectorAll('h2[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavigateToContent = (targetId: Possible<string>) => {
    if (targetId) {
      const element = document.getElementById(targetId);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  };

  const isFreeEventPast =
    !!freeEvent &&
    isDateAfter(
      Date.now(),
      isEventShindig(freeEvent)
        ? freeEvent.date
        : (freeEvent.upcomingSessionsV2?.[0]?.startAt ?? 0),
    );

  const freeEventParams: Nullable<EventBannerParams> = freeEvent
    ? isEventShindig(freeEvent)
      ? {
          title: freeEvent.shindigName,
          time: freeEvent.date,
          duration: freeEvent.shindigDuration,
          url: freeEvent.link,
          coaches: freeEvent.coaches,
          categories: freeEvent.categories?.flatMap((cats) => cats.category),
        }
      : {
          title: freeEvent.className ?? '',
          time: freeEvent.upcomingSessionsV2?.[0].startAt,
          duration: freeEvent.classDuration ?? undefined,
          url:
            APPLICANT_BASE_URL +
            '/' +
            getClassUrl(freeEvent.slug ?? '', !!freeEvent.isFreeEvent),
          coaches: freeEvent.coach ? [freeEvent.coach] : undefined,
          categories: freeEvent.category?.slug
            ? [freeEvent.category]
            : undefined,
        }
    : null;

  const { content, toc } = useMemo(() => {
    let counterOfH2 = 0;
    const toc: TableOfContentsEntry[] = [];

    /* 
      This is from LEL-3496, which is a small test with an advertising vendor on these specific pages only.
      Hopefully we can remove this soon - work with Spencer.
    */
    const esyohTrialPages = {
      'different-types-of-nursing-specialties': true,
      'rn-school-what-you-need-to-know-about-nursing-education-and-careers':
        true,
      'bsn-programs-what-you-need-to-know-about-bachelor-of-science-in-nursing-programs':
        true,
      'nursing-school-cost-how-much-is-nursing-school-and-how-to-finance-your-education':
        true,
      'teas-test-what-it-is-and-how-to-prepare': true,
      'accelerated-nursing-programs-in-the-us': true,
      'how-long-is-nursing-school-breakdown-by-degree-path': true,
      'nursing-resume-guide-with-template-and-examples': true,
      'nursing-cas-everything-you-need-to-know': true,
      '50-nursing-interview-questions-with-sample-answers': true,
      'list-of-masters-in-nursing-programs-in-the-us': true,
      'nursing-prerequisites-what-you-need-to-become-a-nurse': true,
      'most-important-nursing-skills-for-your-resume': true,
      'is-nursing-school-hard-what-to-know': true,
      'nursing-internships-externships-and-fellowships-the-ultimate-guide':
        true,
      'nursing-degree-online-comprehensive-list-of-programs': true,
      'nursing-schools-in-texas': true,
    };

    const isEshoyArticle = !!(esyohTrialPages[articleSlug ?? ''] ?? false);

    // this is short for 'leland plus banner found' because if i make it any longer, I have to double up the eslint/ts-ignore below
    const lpbf = contentProp.some((item) => item._type === 'lelandPlusBanner');
    const content: ContentBlock[] = [];
    contentProp?.forEach((item) => {
      if (item._type === 'bodyText' && item.style === 'h2') {
        const bodyTextItem = item;
        const textContent: Optional<string> = bodyTextItem.children?.reduce(
          (text, child) => {
            if (child.text) {
              return text + child.text;
            }
            return text;
          },
          '',
        );
        if (textContent) {
          toc.push({ id: slugify(textContent), text: textContent });
        }
        if (isEshoyArticle) {
          if (counterOfH2++ === 2) {
            content.push({
              _key: 'esyoh',
              _type: 'esyoh',
            });
          }
        }
        if (!lpbf && !isEshoyArticle) {
          if (counterOfH2++ === 2) {
            content.push({
              _key: 'lelandPlusBanner',
              _type: 'lelandPlusBanner',
            });
          }
        }
      }
      content.push(item);
    });
    if (!lpbf && counterOfH2 < 2 && !isEshoyArticle) {
      content.push({
        _key: 'lelandPlusBanner',
        _type: 'lelandPlusBanner',
      });
    }

    return { content, toc };
  }, [contentProp, articleSlug]);

  const components: Partial<PortableTextReactComponents> = useMemo(() => {
    const categoriesList = categories
      ?.map((c) => {
        const slug = c.slug?.current;
        return slug && categorySlugToUrn(slug);
      })
      .filter(compact);
    return {
      ...baseComponents,
      types: {
        ...baseComponents.types,
        assetDownload: (props) => (
          <AssetDownload categories={categoriesList} {...props} />
        ),
        lelandPlusBanner: (props) =>
          lelandPlusContributors ? (
            <LelandPlusBanner
              articleSlug={articleSlug}
              categories={categoriesList}
              contributors={lelandPlusContributors}
              {...props}
              onClick={() => {
                mxpnl.track('Leland+ Banner Click', {
                  articleSlug: articleSlug,
                  source: 'article',
                });
                phog.capture('article_page--leland_plus_banner_click', {
                  articleSlug: articleSlug,
                  source: 'article',
                });
              }}
            />
          ) : null,
        esyoh: () => (
          <>
            <Script
              src="https://www.esyoh.com/clients/js/widget.js?ver=3.0.0"
              id="esyoh-widget-js"
            />
            <Script
              id="esyoh"
              strategy="lazyOnload"
              dangerouslySetInnerHTML={{
                __html: `
                (function(){
                  let esyLimit = 0;
                  const initESY = function() {
                    if(ESY){
                      ESY.Widget({
                        domain_id: 'joinleland.com',
                        widget_id: '760888'
                      });                  
                    } else {
                      if(esyLimit++ < 5){
                        esyLimit++;
                        setTimeout(initESY, 500);
                      }
                    }
                  }
                  initESY();
                })();
              `,
              }}
            />
            {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
            <div className="esy_widget_container my-6 [&_a]:underline"></div>
          </>
        ),
      },
    };
  }, [articleSlug, categories, lelandPlusContributors]);

  const coachesFromFreeEvent: EventBannerProps['coaches'] =
    freeEventParams?.coaches?.map((coach) => ({
      id: coach.id,
      firstName: coach.user.firstName,
      lastName: coach.user.lastName,
      slug: coach.slug,
      pictureLink: coach.user.pictureLink ?? undefined,
      url: getCoachProfileUrl(
        coach.slug,
        getCategoryForEvent(
          coach.categoryInformationList?.map(
            (catInfo) => catInfo.category.slug,
          ) ?? [],
          freeEventParams.categories?.map((cat) => cat.slug) ?? [],
        ),
      ),
    }));

  return content.length > 0 ? (
    <div
      className={`flex flex-col ${toc.length > 0 ? 'lg:justify-between' : 'justify-center'} w-full gap-10 lg:flex-row-reverse lg:gap-20 lg:border-t lg:border-leland-gray-stroke lg:pt-10`}
    >
      <div className="lg:hidden">
        <ArticleCTA articleSlug={articleSlug} categories={categories} />
      </div>
      {toc.length > 0 ? (
        <>
          <div className="h-max lg:hidden">
            <TableOfContents
              defaultCollapsed={true}
              entries={toc}
              activeSectionId={activeSectionId}
              handleNavigateToContent={handleNavigateToContent}
            />
          </div>
          <div className="sticky top-5 hidden h-max max-w-96 space-y-8 lg:block">
            <TableOfContents
              defaultCollapsed={false}
              entries={toc}
              activeSectionId={activeSectionId}
              handleNavigateToContent={handleNavigateToContent}
            />
            <ArticleCTA articleSlug={articleSlug} categories={categories} />
          </div>
        </>
      ) : null}
      <div className="space-y-10 lg:min-w-0">
        {freeEventParams?.time &&
        freeEventParams.duration &&
        !isFreeEventPast ? (
          <div className="mx-auto w-full lg:max-w-4xl">
            <EventBanner
              title={freeEventParams.title}
              time={freeEventParams.time}
              duration={freeEventParams.duration}
              eventUrl={addUtmParamsFromArticle(
                freeEventParams.url,
                articleSlug,
              )}
              coaches={coachesFromFreeEvent}
              onClick={() => {
                mxpnl.track('Event Banner Click', {
                  categorySlug: getCategoryForEvent(
                    categories
                      ?.map((category) => category.slug?.current)
                      .filter(compact) ?? [],
                    freeEventParams.categories?.map((cat) => cat.slug) ?? [],
                  ),
                  articleSlug: articleSlug,
                  source: 'article',
                });
                phog.capture('article_page--event_banner_click', {
                  categorySlug: getCategoryForEvent(
                    categories
                      ?.map((category) => category.slug?.current)
                      .filter(compact) ?? [],
                    freeEventParams.categories?.map((cat) => cat.slug) ?? [],
                  ),
                  articleSlug: articleSlug,
                  source: 'article',
                });
              }}
            />
          </div>
        ) : null}
        <div>
          <PortableText value={content} components={components} />
        </div>
        {coach ? (
          <ArticleAuthor
            coach={coach}
            categorySlug={categories?.[0]?.slug?.current}
          />
        ) : null}
      </div>
    </div>
  ) : null;
};

export const BodyContentHtml: FC<{ content: ContentBlock[] }> = ({
  content,
}) => <PortableText value={content} components={baseHtmlComponents} />;

export default BodyContent;
